import { useEffect, useRef, useState } from 'react'
import callApi from 'utils/callApi'
import { getState } from 'common/Footer/helpers'
import {
  LOCATION_FETCH_STATUS,
  USER_GEO_IP_LOCATION_KEY,
} from 'constants/common'
import { getFirstLocationResultData } from 'utils/common'
import { isServer } from 'utils/environment'
import {
  getBrowserCoordinates,
  isUsProviderSearchLocation,
  reverseGeocodeCoordinates,
} from 'utils/browserGeolocation'
import {
  getProviderSearchPlacesRequest,
  pickProviderSearchLocationPrediction,
} from 'utils/providerSearchLocation'

type UseFecthLocationReturnType = {
  locationData: GoogleLocationResult
  browserLocationData: GoogleLocationResult
  hasOutsideUsBrowserLocation: boolean
  error: AlertMessage
  loading: boolean
  location: string
  status: string
}

type UseFetchLocationParamType = {
  locationString?: string
  fetchLocationEnabled?: boolean
  usedForFeature?: string
  /**
   * When true and no locationString is provided, try browser geolocation first
   * (permission prompt on first visit / silent fetch when already granted).
   * Falls back to the existing IP CDN → Places flow on deny/unavailable/non-US.
   *
   * @shared Used by Provider Search SearchPage. Keep opt-in so other callers
   * (Navbar, PreSearch, SE hubs) retain IP-only auto-select.
   */
  isBrowserGeolocationPreferred?: boolean
}

/* To use this hook in a component, wrap the component with withGoogleMapsScript HOC and provide
 * google's places library as props to the HOC
 **/
/*
 * Little briefing on this hook:
 * This hook allows you to get the location data from a locationString like "Florida, USA".
 * The location data that it returns contains latitude, longitude, state.
 * It uses google places API to get a list of suggestions from the string
 * Then it will use the GeoCode API to get the latitude and longitude from the first item of the suggestions.
 *
 * If we are not passing in the locationString then it would use g.thriveworks.com to fetch the location string using ip location find.
 * and then make the google places API call from that string. and follow the same procedure as above.
 *
 * When isBrowserGeolocationPreferred is true and locationString is absent, the hook requests the
 * browser position first, reverse-geocodes it, and only then falls back to IP.
 **/
const useFetchLocation = ({
  // This will be the default value for locationString,
  // If this value is not present, it means that the calling function has supplied a value for locationString
  locationString = null,
  fetchLocationEnabled = true,
  usedForFeature,
  isBrowserGeolocationPreferred = false,
}: UseFetchLocationParamType): UseFecthLocationReturnType => {
  const [location, setLocation] = useState<string>(null)
  const [error, setError] = useState<AlertMessage>(null)
  const [locationData, setLocationData] = useState<GoogleLocationResult>(null)
  const [
    browserLocationData,
    setBrowserLocationData,
  ] = useState<GoogleLocationResult>(null)
  const [
    hasOutsideUsBrowserLocation,
    setHasOutsideUsBrowserLocation,
  ] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(fetchLocationEnabled)
  const [status, setStatus] = useState<string>(null)
  const hasBrowserLocationSucceeded = useRef(false)

  const onFoundSuggestions = async (
    predictions: google.maps.places.AutocompletePrediction[],
    status: google.maps.places.PlacesServiceStatus,
    requestedLocation: string
  ) => {
    try {
      if (
        status !== google.maps.places.PlacesServiceStatus.OK ||
        !predictions
      ) {
        setStatus(LOCATION_FETCH_STATUS.empty)
        setError({
          message: 'Zero results found',
          timestamp: new Date().getTime(),
        })
        setLoading(false)
        return
      }

      const {
        place_id,
        structured_formatting,
      } = pickProviderSearchLocationPrediction(predictions, requestedLocation)

      const { firstResult, latLong } = await getFirstLocationResultData(
        place_id,
        usedForFeature
      )
      const state = getState(firstResult)
      setLocationData({
        ...firstResult,
        ...latLong,
        ...structured_formatting,
        state,
      })
    } catch (error) {
      setError({
        message: 'Something went wrong, please try later',
        timestamp: new Date().getTime(),
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchLocationDataFromGoogle = (location: string) => {
    if (location) {
      const service = new google.maps.places.AutocompleteService()
      const { input, types } = getProviderSearchPlacesRequest(location)

      service.getPlacePredictions(
        {
          input,
          types,
          componentRestrictions: {
            country: 'us',
          },
        },
        (predictions, status) =>
          onFoundSuggestions(predictions, status, location)
      )
    }
  }

  const fetchLocationFromCDN = async () => {
    try {
      const response = await callApi<string>({
        baseURL: process.env.NEXT_PUBLIC_LOCATION_FETCH_URL,
        considerMandatoryHeaders: false,
      })

      if (hasBrowserLocationSucceeded.current) {
        return
      }

      if (response) {
        sessionStorage.setItem(USER_GEO_IP_LOCATION_KEY, response)
      }

      setLocation(response)

      if (!response) {
        setLoading(false)
        return
      }

      fetchLocationDataFromGoogle(response)
    } catch {
      if (!hasBrowserLocationSucceeded.current) {
        setLoading(false)
      }
    }
  }

  const applyResolvedLocation = (resolved: GoogleLocationResult) => {
    const locationName = resolved.formatted_address
    hasBrowserLocationSucceeded.current = true

    if (locationName) {
      sessionStorage.setItem(USER_GEO_IP_LOCATION_KEY, locationName)
    }

    setLocation(locationName)
    setBrowserLocationData(resolved)
    setStatus(LOCATION_FETCH_STATUS.success)
    setLoading(false)
  }

  const fetchLocationFromBrowser = async () => {
    try {
      const { latitude, longitude } = await getBrowserCoordinates()
      const resolved = await reverseGeocodeCoordinates(
        latitude,
        longitude,
        usedForFeature
      )

      if (isUsProviderSearchLocation(resolved)) {
        applyResolvedLocation(resolved)
        return
      }

      if (resolved) {
        setHasOutsideUsBrowserLocation(true)
        setLoading(false)
      }
    } catch {
      // Permission denied or browser location unavailable — keep CDN location.
    }
  }

  useEffect(() => {
    try {
      if (fetchLocationEnabled) {
        setLoading(true)

        if (window.google) {
          // This will avoid any unnecessary calls to google places API, through the else block

          if (locationString !== null) {
            fetchLocationDataFromGoogle(locationString)
          } else if (isBrowserGeolocationPreferred) {
            fetchLocationFromCDN()
            fetchLocationFromBrowser()
          } else {
            fetchLocationFromCDN()
          }
        }
      } else {
        setLoading(false)
      }
    } catch (error) {
      setLoading(false)
      setError({
        timestamp: new Date().getTime(),
        message: error?.message ?? 'Something went wrong',
      })
    }
  }, [!isServer() ? window.google : null, locationString, fetchLocationEnabled])

  return {
    locationData,
    browserLocationData,
    hasOutsideUsBrowserLocation,
    error,
    loading,
    location,
    status,
  }
}

export default useFetchLocation
